/**
 * Les lectures du tableau de bord comptable, et elles seules.
 *
 * « Qu'est-ce que la comptable doit traiter aujourd'hui ? » — à partir de ce
 * qui existe réellement : le journal des ventes, son contrôle, les écritures
 * en brouillon, la file de rapprochement, la reprise d'ouverture. Aucun geste
 * n'est lancé d'ici : **le journal des ventes ne se relance pas parce que la
 * page s'ouvre** (cadrage, § 20), il se relance sur accord.
 */

import { db } from '../payments/db';
import { enClair } from '../compta/reprise/erreur';
import {
  lireLaReprise, lireLaSynthese, lireLesEcartsClients, lireLesNotesDeTransition,
} from '../compta/reprise/gateway.supabase';
import { bloquants, type Bloquant, type StatutReprise } from '../compta/reprise/types';

const compta = () => db.schema('compta');

function echec(e: unknown, defaut: string): never {
  throw new Error(enClair(e, defaut));
}

/** Les libellés des anomalies de `compta.v_controle_des_ventes`. */
export const ANOMALIES_DES_VENTES: Record<string, string> = {
  a_comptabiliser: 'à comptabiliser',
  ecart: 'comptabilisées pour un autre montant',
  tiers_different: 'sur un autre tiers',
  comptabilisee_hors_perimetre: 'comptabilisées hors périmètre',
  divergence_grand_livre_famille: 'en écart avec le grand livre des familles',
};

export interface PieceEnEcart {
  sourceType: string;
  numero: string | null;
  datePiece: string | null;
  anomalie: string;
  attenduCents: number | null;
}

export interface EtatDesVentes {
  ecrituresValidees: number;
  /** Par anomalie, dans l'ordre de `ANOMALIES_DES_VENTES`, puis les autres. */
  anomalies: { anomalie: string; nombre: number }[];
  /** Les premières pièces en écart, pour les retrouver. */
  exemples: PieceEnEcart[];
  /** Au-delà de mille lignes de contrôle, le détail n'est pas complet. */
  tronque: boolean;
}

export async function lireLEtatDesVentes(): Promise<EtatDesVentes> {
  const [ecritures, controle] = await Promise.all([
    compta().from('ecritures').select('id', { count: 'exact', head: true })
      .eq('journal_code', 'VEN').eq('statut', 'validee'),
    compta().from('v_controle_des_ventes')
      .select('source_type, piece_numero, date_piece, anomalie, attendu_cents', { count: 'exact' })
      .order('date_piece', { ascending: false, nullsFirst: false })
      .limit(1000),
  ]);
  if (ecritures.error) echec(ecritures.error, 'Écritures illisibles');
  if (controle.error) echec(controle.error, 'Contrôle des ventes illisible');

  const parAnomalie = new Map<string, number>();
  const lignes = (controle.data ?? []) as Record<string, unknown>[];
  for (const l of lignes) {
    const a = String(l.anomalie);
    parAnomalie.set(a, (parAnomalie.get(a) ?? 0) + 1);
  }
  const ordre = Object.keys(ANOMALIES_DES_VENTES);
  const anomalies = [...parAnomalie.entries()]
    .map(([anomalie, nombre]) => ({ anomalie, nombre }))
    .sort((a, b) => {
      const ia = ordre.indexOf(a.anomalie);
      const ib = ordre.indexOf(b.anomalie);
      return (ia < 0 ? ordre.length : ia) - (ib < 0 ? ordre.length : ib);
    });

  return {
    ecrituresValidees: ecritures.count ?? 0,
    anomalies,
    exemples: lignes.slice(0, 5).map((l) => ({
      sourceType: String(l.source_type ?? ''),
      numero: (l.piece_numero as string | null) ?? null,
      datePiece: (l.date_piece as string | null) ?? null,
      anomalie: String(l.anomalie),
      attenduCents: l.attendu_cents === null || l.attendu_cents === undefined ? null : Number(l.attendu_cents),
    })),
    tronque: (controle.count ?? 0) > lignes.length,
  };
}

/** Le seul nombre de pièces de vente que le journal n'a pas encore portées. */
export async function compterLesPiecesAComptabiliser(): Promise<number> {
  const { count, error } = await compta()
    .from('v_controle_des_ventes')
    .select('piece_id', { count: 'exact', head: true })
    .eq('anomalie', 'a_comptabiliser');
  if (error) echec(error, 'Contrôle des ventes illisible');
  return count ?? 0;
}

export interface EtatDuNoyau {
  brouillons: number;
  exercices: { annee: number; statut: string }[];
}

export async function lireLEtatDuNoyau(): Promise<EtatDuNoyau> {
  const [brouillons, exercices] = await Promise.all([
    compta().from('ecritures').select('id', { count: 'exact', head: true }).eq('statut', 'brouillon'),
    compta().from('exercices').select('annee, statut').order('annee', { ascending: true }),
  ]);
  if (brouillons.error) echec(brouillons.error, 'Écritures illisibles');
  if (exercices.error) echec(exercices.error, 'Exercices illisibles');
  return {
    brouillons: brouillons.count ?? 0,
    exercices: ((exercices.data ?? []) as { annee: number; statut: string }[]).map((e) => ({
      annee: Number(e.annee),
      statut: e.statut,
    })),
  };
}

export interface EtatDeLaReprise {
  annee: number | null;
  /** `null` : aucune reprise ouverte. */
  statut: StatutReprise | null;
  /** Ce qui empêche la validation, calculé comme l'écran de la reprise le calcule. */
  bloquants: Bloquant[];
}

/**
 * Le statut de la reprise d'ouverture, et ce qui la bloque — les mêmes
 * lectures et la même règle (`bloquants`) que l'écran de la reprise. Rien
 * n'est validé ni comptabilisé d'ici.
 */
export async function lireLEtatDeLaReprise(): Promise<EtatDeLaReprise> {
  const { exercice, reprise } = await lireLaReprise();
  if (!reprise) return { annee: exercice?.annee ?? null, statut: null, bloquants: [] };
  if (reprise.statut !== 'brouillon') return { annee: exercice?.annee ?? null, statut: reprise.statut, bloquants: [] };
  const [synthese, notes, ecartsClients] = await Promise.all([
    lireLaSynthese(reprise.id),
    lireLesNotesDeTransition(reprise.id),
    lireLesEcartsClients(),
  ]);
  return {
    annee: exercice?.annee ?? null,
    statut: reprise.statut,
    bloquants: bloquants({ synthese, notes, ecartsClients }),
  };
}
